import { isRecord } from '@/utils/objects';
import { parseLogType } from './activity-log-types';
import type { ActivityLog } from './types';

/**
 * The copyable/normalized event data for a log. Returns `undefined` for types that shouldn't
 * expose Copy JSON — that undefined is what gates the "Copy JSON" button (many types, e.g.
 * sent/opened/delivered actions, profile delete/merge, segment events, page/screen, have none).
 */

// Types whose `attrs` are copied 1:1.
const DIRECT_TYPES = new Set([
  'event',
  'failed_event',
  'failed_llm_action',
  'webhook_event',
  '_o:*:create',
  'profile_create',
  'add_relationship',
  '_o:*:add_relationship',
  'failed_attribute_change',
  '_o:*:failed_attribute_change',
]);

// Attribute-change types: strip the `{ from, to }` depth to just the `to` value.
const ATTRIBUTE_CHANGE_TYPES = new Set(['attribute_change', '_o:*:attribute_change']);
const RELATIONSHIP_ATTRIBUTE_TYPES = new Set(['_r:*:attribute_change', '_r:*:failed_attribute_change']);

function asRecord(value: unknown): Record<string, unknown> | null {
  return isRecord(value) ? value : null;
}

/** Collapse `{ [attr]: { from, to } }` → `{ [attr]: to }` (dropping entries without a `to`). */
function toValues(source: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(source)) {
    const nested = asRecord(value);
    if (nested != null && nested.to != null) result[key] = nested.to;
  }
  return result;
}

export function activityLogJson(log: ActivityLog): unknown {
  const parsed = parseLogType(log.type);

  if (DIRECT_TYPES.has(parsed)) return log.attrs ?? undefined;

  if (ATTRIBUTE_CHANGE_TYPES.has(parsed)) {
    const attrs = asRecord(log.attrs);
    return attrs == null ? undefined : toValues(attrs);
  }

  if (RELATIONSHIP_ATTRIBUTE_TYPES.has(parsed)) {
    const attrs = asRecord(log.attrs);
    if (attrs == null) return undefined;
    const updates = asRecord(attrs.attribute_updates);
    return updates == null ? { ...attrs } : { ...attrs, attribute_updates: toValues(updates) };
  }

  return undefined;
}
